import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  FormSchemaVersion,
  BespokeTemplateResponse,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { FormTreeQuestion } from './formSchemaTypes';
import { formatSectionDropdownLabel } from './formatSectionDropdownLabel';
import {
  bespokeApiErrorMessage,
  pinFromFormSchemaVersion,
} from './formSchemaUtils';
import {
  applyMatchingLayoutCopyFromSource,
  fetchMatchingLiveLayoutSourcesForSection,
  formatMatchingCopyAttribution,
  type MatchingLiveLayoutSource,
} from './matchingLayoutCopyShared';

export type MatchingLayoutCopyToast = {
  error: (title: string, description?: string) => void;
  success: (title: string, description?: string) => void;
};

export type { MatchingLiveLayoutSource };

export { formatMatchingCopyAttribution };

export function useMatchingLayoutCopyDialog({
  selectedVersion,
  selectedRogerDocFormType,
  existingTemplateBySection,
  toast,
  onTemplateSaved,
}: {
  selectedVersion: FormSchemaVersion | null;
  selectedRogerDocFormType: RogerDocFormType | null;
  existingTemplateBySection: Map<string, BespokeTemplateResponse>;
  toast: MatchingLayoutCopyToast;
  /** Patch local state with the saved row (avoids refetching the full list). */
  onTemplateSaved: (saved: BespokeTemplateResponse) => void;
}) {
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [matchingCopyOpen, setMatchingCopyOpen] = useState(false);
  const [matchingCopySectionName, setMatchingCopySectionName] = useState<
    string | null
  >(null);
  const [matchingCopyQuestions, setMatchingCopyQuestions] = useState<
    FormTreeQuestion[] | null
  >(null);
  const [matchingCopyScanning, setMatchingCopyScanning] = useState(false);
  const [matchingCopyMatches, setMatchingCopyMatches] = useState<
    MatchingLiveLayoutSource[]
  >([]);
  const [matchingCopyError, setMatchingCopyError] = useState<string | null>(
    null
  );
  const [matchingCopySelectedTemplateId, setMatchingCopySelectedTemplateId] =
    useState('');
  const [matchingCopyApplying, setMatchingCopyApplying] = useState(false);

  const closeMatchingCopyDialog = useCallback(() => {
    setMatchingCopyOpen(false);
    setMatchingCopySectionName(null);
    setMatchingCopyQuestions(null);
    setMatchingCopyMatches([]);
    setMatchingCopyError(null);
    setMatchingCopySelectedTemplateId('');
    setMatchingCopyScanning(false);
  }, []);

  const openMatchingCopyDialog = useCallback(
    (sectionName: string, questions: FormTreeQuestion[]) => {
      if (!selectedVersion || !selectedRogerDocFormType) {
        toast.error(
          'Pick a schema version',
          'Select a form type and schema version first.'
        );
        return;
      }
      setMatchingCopySectionName(sectionName);
      setMatchingCopyQuestions(questions);
      setMatchingCopyMatches([]);
      setMatchingCopyError(null);
      setMatchingCopySelectedTemplateId('');
      setMatchingCopyOpen(true);
    },
    [selectedRogerDocFormType, selectedVersion, toast]
  );

  useEffect(() => {
    if (
      !matchingCopyOpen ||
      !matchingCopySectionName ||
      !matchingCopyQuestions ||
      matchingCopyQuestions.length === 0 ||
      !selectedVersion
    ) {
      return;
    }

    let cancelled = false;
    void (async () => {
      setMatchingCopyScanning(true);
      setMatchingCopyError(null);
      try {
        const destinationPin = pinFromFormSchemaVersion(
          selectedRogerDocFormType!,
          selectedVersion
        );
        if (!destinationPin) {
          setMatchingCopyMatches([]);
          setMatchingCopyError(
            'The selected schema is missing a version identifier.'
          );
          return;
        }
        const deduped = await fetchMatchingLiveLayoutSourcesForSection({
          destinationSectionName: matchingCopySectionName,
          destinationFormSchemaVersionPin: destinationPin,
          questions: matchingCopyQuestions,
        });
        if (cancelled) return;
        setMatchingCopyMatches(deduped);
      } catch (err) {
        if (cancelled) return;
        console.error('Matching layout lookup failed:', err);
        setMatchingCopyMatches([]);
        setMatchingCopyError(bespokeApiErrorMessage(err));
      } finally {
        if (!cancelled) setMatchingCopyScanning(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    matchingCopyOpen,
    matchingCopyQuestions,
    matchingCopySectionName,
    selectedRogerDocFormType,
    selectedVersion,
  ]);

  useEffect(() => {
    if (matchingCopyMatches.length > 0) {
      setMatchingCopySelectedTemplateId(matchingCopyMatches[0]!.template.id);
    } else {
      setMatchingCopySelectedTemplateId('');
    }
  }, [matchingCopyMatches]);

  const handleApplyMatchingCopy = useCallback(async () => {
    if (
      !selectedVersion ||
      !selectedRogerDocFormType ||
      !matchingCopySectionName ||
      !matchingCopyQuestions ||
      matchingCopyQuestions.length === 0
    ) {
      return;
    }
    const match = matchingCopyMatches.find(
      m => m.template.id === matchingCopySelectedTemplateId
    );
    if (!match?.template.layout_json) {
      toast.error('Pick a source', 'Select a matching live layout.');
      return;
    }

    setMatchingCopyApplying(true);
    try {
      const saved = await applyMatchingLayoutCopyFromSource({
        match,
        destinationSectionName: matchingCopySectionName,
        questions: matchingCopyQuestions,
        selectedRogerDocFormType,
        selectedVersion,
        existingTemplateBySection,
      });
      const displayName = formatSectionDropdownLabel(matchingCopySectionName);
      toast.success(
        'Layout copied',
        `${displayName}: ${formatMatchingCopyAttribution(match)}`
      );
      onTemplateSaved(saved);
      closeMatchingCopyDialog();
    } catch (err) {
      toast.error('Copy failed', bespokeApiErrorMessage(err));
    } finally {
      setMatchingCopyApplying(false);
    }
  }, [
    closeMatchingCopyDialog,
    existingTemplateBySection,
    onTemplateSaved,
    matchingCopyMatches,
    matchingCopyQuestions,
    matchingCopySectionName,
    matchingCopySelectedTemplateId,
    selectedRogerDocFormType,
    selectedVersion,
    toast,
  ]);

  return {
    matchingCopyOpen,
    matchingCopySectionName,
    matchingCopyScanning,
    matchingCopyMatches,
    matchingCopyError,
    matchingCopySelectedTemplateId,
    setMatchingCopySelectedTemplateId,
    matchingCopyApplying,
    openMatchingCopyDialog,
    closeMatchingCopyDialog,
    handleApplyMatchingCopy,
  };
}
